import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import {
  MarketingShellLayout,
  ValueGrid,
  type ValueItem,
} from "../../components/marketing/MarketingShell";
import { Seo } from "../../seo/Seo";
import { trackCtaClick } from "../../lib/analytics";

const VALUES: ValueItem[] = [
  {
    tag: "SCHEMA",
    title: "Paste JSON, get a URL.",
    body: "Paste a response shape. Use {{faker.name}} or {{params.id}} for dynamic fields. Save. The URL is live and shareable in seconds.",
  },
  {
    tag: "VARIANTS",
    title: "404 and 500 are first-class.",
    body: "One endpoint, many responses. Switch variants by query, header, or weighted random. Test your error states without breaking staging.",
  },
  {
    tag: "REALISTIC",
    title: "Latency, CORS, status codes.",
    body: "Dial in realistic latency (the spinner is a feature). Set headers. Turn CORS on. Anything that bites you in production, you can simulate here first.",
  },
];

const TOUR = [
  { t: 0, l: "Paste JSON", d: "Drop your expected response into the editor." },
  { t: 5, l: "Save", d: "A real URL is generated under your project namespace." },
  { t: 10, l: "Share", d: "Send the URL to your frontend. They’re unblocked." },
  {
    t: 15,
    l: "Iterate",
    d: "Add variants, tweak latency, watch hits in the log.",
  },
];

export function MocksMarketingPage() {
  return (
    <MarketingShellLayout active="API mocks">
      <Seo route="/mocks" />
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 pt-12 sm:pt-16 pb-10 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase text-signal-warn">
              <span className="size-1.5 rounded-sm bg-signal-warn" />
              03 · API mocks · for the unblocking side of building
            </span>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-semibold leading-[0.96] tracking-[-0.04em]">
              A real URL,
              <br />
              <span className="font-serif italic font-normal">
                real-shaped
              </span>{" "}
              <span className="text-signal-warn">JSON.</span>
              <br />
              Fifteen seconds.
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl font-serif">
              The frontend shouldn’t wait on the backend. Paste a schema, hit
              save, get a shareable URL with realistic data — faker built in,
              latency dialed to taste.
            </p>
            <div className="mt-7 flex flex-wrap gap-3 items-center">
              <Link
                to="/register"
                onClick={() =>
                  trackCtaClick("hero", {
                    target: "register",
                    surface: "API mocks",
                  })
                }
              >
                <Button size="lg">Start free →</Button>
              </Link>
              <a
                href="/#playground"
                onClick={() =>
                  trackCtaClick("sub_nav", { surface: "API mocks" })
                }
                className="text-sm font-medium underline underline-offset-4 decoration-rule hover:decoration-foreground"
              >
                Try the live demo →
              </a>
            </div>
          </div>

          {/* Hero — mock card */}
          <div className="self-start w-full">
            <div className="bg-card-paper border border-rule rounded-xl overflow-hidden shadow-[0_18px_42px_-22px_rgba(27,31,42,0.18)]">
              <div className="px-3.5 py-2.5 border-b border-rule-soft bg-panel flex items-center gap-2.5">
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-signal-ok-soft text-signal-ok">
                  GET
                </span>
                <span className="font-mono text-[11.5px] flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  mock.swisstools.dev/
                  <span className="text-faint">morpheus/</span>customer/:id
                </span>
                <span className="font-mono text-[10px] text-signal-ok">
                  ● live
                </span>
              </div>
              <pre className="m-0 px-5 py-4 font-mono text-[12.5px] leading-[1.65] bg-card-paper overflow-auto">
{`{
  `}
                <span className="text-signal-red">"id"</span>
                {`: `}
                <span className="text-signal-ok">"cus_8x9k2L"</span>
                {`,
  `}
                <span className="text-signal-red">"email"</span>
                {`: `}
                <span className="text-signal-ok">"maria@morpheus.dev"</span>
                {`,
  `}
                <span className="text-signal-red">"plan"</span>
                {`: {
    `}
                <span className="text-signal-red">"id"</span>
                {`: `}
                <span className="text-signal-ok">"team-pro"</span>
                {`,
    `}
                <span className="text-signal-red">"seats"</span>
                {`: `}
                <span className="text-signal-warn">12</span>
                {`,
    `}
                <span className="text-signal-red">"renews_at"</span>
                {`: `}
                <span className="text-signal-ok">"2026-04-12"</span>
                {`
  },
  `}
                <span className="text-signal-red">"created_at"</span>
                {`: `}
                <span className="text-signal-ok">"2024-08-14T09:32:00Z"</span>
                {`
}`}
              </pre>
              <div className="px-3.5 py-2.5 border-t border-rule-soft font-mono text-[10.5px] text-faint bg-panel flex justify-between">
                <span>200 · happy path · 120ms latency</span>
                <span className="text-signal-red">3 variants</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValueGrid items={VALUES} accent="var(--color-signal-warn)" />

      {/* 15-second tour */}
      <section className="border-t border-rule bg-card-paper">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-10 sm:py-12">
          <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-faint mb-4">
            15-second tour
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TOUR.map((s, i) => (
              <div
                key={s.t}
                className="relative p-4 bg-paper border border-rule rounded-xl"
              >
                <div className="font-mono text-[28px] font-semibold text-signal-warn tracking-tight mb-1">
                  {s.t}
                  <span className="text-sm text-faint">s</span>
                </div>
                <div className="text-sm font-semibold mb-1">{s.l}</div>
                <p className="text-xs text-muted-foreground m-0 leading-relaxed">
                  {s.d}
                </p>
                {i < TOUR.length - 1 && (
                  <span className="hidden lg:block absolute -right-3.5 top-1/2 font-mono text-base text-faint">
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingShellLayout>
  );
}
